import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Divider,
  Collapse,
  ListItemSecondaryAction,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate } from 'react-router-dom';
import type { CartItem } from '../types/menu';
import type { User } from '../store/slices/userSlice';
import Loader from './Loader';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { trackEvent } from '../utils/analytics';

interface CartDrawerProps {
  open: boolean;
  cart: CartItem[];
  total: number;
  onClose: () => void;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  onCheckout: () => void;
  onRemoveGroup?: (groupCharacteristics: { menuItemId: string; notes?: string; selectedOptions?: CartItem['selectedOptions'] }) => void;
  itemActionLoading?: { [key: string]: boolean };}

const groupCartItems = (cart: CartItem[]) => {
  // First group by user
  const userGroups: { [key: string]: CartItem[] } = {};
  cart.forEach(item => {
    // Extract user from item.user or item.orderItemUser?.user
    const user = item.user || item.orderItemUser?.user;
    const userId = user?.id || 'unknown';
    if (!userGroups[userId]) {
      userGroups[userId] = [];
    }
    // Attach user to item for downstream code
    userGroups[userId].push({ ...item, user });
  });

  // Then group items within each user group
  const groupedByUser: { [key: string]: { [key: string]: CartItem } } = {};
  Object.entries(userGroups).forEach(([userId, items]) => {
    const grouped: { [key: string]: CartItem } = {};
    items.forEach(item => {
      const optionsKey = item.selectedOptions 
        ? JSON.stringify(Object.entries(item.selectedOptions)
              .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
              .map(([groupName, choices]) => ({
                groupName,
                choices: choices.slice().sort((a, b) => a.name.localeCompare(b.name))
              }))
            ) 
        : '';
      const key = `${item.menuItemId}-${item.notes || ''}-${optionsKey}`;
      if (grouped[key]) {
        grouped[key].quantity += item.quantity;
      } else {
        grouped[key] = { ...item };
      }
    });
    groupedByUser[userId] = grouped;
  });

  return groupedByUser;
};

const CartDrawer: React.FC<CartDrawerProps> = ({
  open,
  cart,
  total,
  onClose,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
  onRemoveGroup,
  itemActionLoading = {},
}) => {
  const [expandedUsers, setExpandedUsers] = useState<Record<string, boolean>>({});
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const currentOrder = useSelector((state: RootState) => state.order.currentOrder);

  const getGroupKey = (item: CartItem) => {
    const optionsKey = item.selectedOptions
      ? JSON.stringify(
          Object.entries(item.selectedOptions)
            .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
            .map(([groupName, choices]) => ({
              groupName,
              choices: choices.slice().sort((a, b) => a.name.localeCompare(b.name)),
            }))
        )
      : '';
    return `${item.menuItemId}-${item.notes || ''}-${optionsKey}`;
  };

  const toggleExpandUser = (userId: string) => {
    setExpandedUsers(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const toggleExpandItem = (groupKey: string) => {
    setExpandedItems(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const groupedCart = useMemo(() => {
    const grouped = groupCartItems(cart);
    
    // Sort the user groups to prioritize current user
    const sortedEntries = Object.entries(grouped).sort(([userIdA], [userIdB]) => {
      if (currentUser && userIdA === currentUser.id) return -1;
      if (currentUser && userIdB === currentUser.id) return 1;
      return 0;
    });

    return Object.fromEntries(sortedEntries);
  }, [cart, currentUser]);

  // Initialize expanded state when drawer opens
  useEffect(() => {
    if (open) {
      const initialExpanded: Record<string, boolean> = {};
      Object.keys(groupedCart).forEach(userId => {
        // Only expand the current user's items by default
        initialExpanded[userId] = currentUser ? userId === currentUser.id : false;
      });
      setExpandedUsers(initialExpanded);
    }
  }, [open, cart.length, currentUser]);

  // Clean up expanded state when user groups are removed
  useEffect(() => {
    const currentUserIds = Object.keys(groupedCart);
    
    setExpandedUsers(prev => {
      const newExpanded = { ...prev };
      let hasChanges = false;
      
      currentUserIds.forEach(userId => {
        if (!(userId in newExpanded)) {
          // Only expand the current user's items by default
          newExpanded[userId] = currentUser ? userId === currentUser.id : false;
          hasChanges = true;
        }
      });
      
      Object.keys(newExpanded).forEach(userId => {
        if (!currentUserIds.includes(userId)) {
          delete newExpanded[userId];
          hasChanges = true;
        }
      });

      return hasChanges ? newExpanded : prev;
    });
  }, [cart, groupedCart, currentUser]);

  const handleEditItem = (item: CartItem) => {
    if (currentOrder && item.id) {
      navigate(`/customize-item/${currentOrder.id}/${item.id}`);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { height: '100dvh', maxHeight: '100dvh' } }}>
      <Box sx={{ width: { xs: '99vw', sm: 420 }, display: 'flex', flexDirection: 'column', height: '100dvh', p: 0 }}>
        {/* Header */}
        <Box sx={{ p: 3, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
            Tu Carrito
          </Typography>
          <IconButton onClick={onClose} size="large" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        {/* Scrollable Cart Items */}
        <Box sx={{ flex: '1 1 0%', overflowY: 'auto', minHeight: 0, px: 1, pb: 'calc(80px + env(safe-area-inset-bottom))' }}>
          <List>
            {Object.keys(groupedCart).length === 0 ? (
              <Typography color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                Tu carrito está vacío.
              </Typography>
            ) : (
              Object.entries(groupedCart).map(([userId, userItems]) => {
                const userObj = Object.values(userItems)[0]?.user;
                const userName = userObj?.name || 'Usuario Desconocido';
                const isUserExpanded = expandedUsers[userId] ?? true;
                const userTotal = Object.values(userItems).reduce(
                  (sum, item) => sum + (item.menuItem.price * item.quantity),
                  0
                );

                return (
                  <React.Fragment key={userId}>
                    <ListItem
                      button
                      onClick={() => toggleExpandUser(userId)}
                      sx={{
                        py: 1,
                        px: 2,
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 500 }}>{userName}</Typography>                          
                        </Box>
                        <Typography sx={{ mr: 1, color: 'text.secondary' }}>
                          ₡{userTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </Typography>
                        <IconButton size="small">
                          {isUserExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                    </ListItem>
                    <Collapse in={isUserExpanded}>
                      {Object.values(userItems).map((item) => {
                        const groupKey = getGroupKey(item);
                        const isExpanded = !!expandedItems[groupKey];
                        const hasOptions = item.selectedOptions && Object.keys(item.selectedOptions).length > 0;

                        return (
                          <React.Fragment key={groupKey}>
                            <ListItem 
                              data-testid="cart-item"
                              alignItems="flex-start" 
                              sx={{ mb: 2, p: 0, pr: 1.5, alignItems: 'center' }}
                            >
                              {item.menuItem.imageUrl && (
                                <Box 
                                  sx={{ 
                                    width: 80, 
                                    height: 80, 
                                    mr: 2, 
                                    borderRadius: 2, 
                                    overflow: 'hidden', 
                                    boxShadow: '0 1px 4px rgba(60,72,100,0.10)', 
                                    flexShrink: 0, 
                                    background: '#f6f6f8', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => handleEditItem(item)}
                                >
                                  <img src={item.menuItem.imageUrl} alt={item.menuItem.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                </Box>
                              )}
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  justifyContent: 'center', 
                                  flex: 1, 
                                  minWidth: 0,
                                  cursor: 'pointer',
                                }}
                                onClick={() => handleEditItem(item)}
                              >
                                <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#181818', lineHeight: 1.2, wordBreak: 'break-word' }}>{item.menuItem.name}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
                                  ₡{(item.menuItem.price * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </Typography>

                                {/* Preview of customizations when collapsed */}
                                {hasOptions && !isExpanded && (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: 'text.secondary',
                                      fontSize: '0.8rem',
                                      mt: 0.5,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {Object.entries(item.selectedOptions || {})
                                      .map(([groupName, choices]) => choices.map(choice => choice.name).join(', '))
                                      .filter(Boolean)
                                      .join(' • ')
                                    }
                                  </Typography>
                                )}

                                {hasOptions && (
                                  <Box 
                                    sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      mt: 0.5,
                                      cursor: 'pointer',
                                      ml: -0.5,
                                      p: 0.5,
                                      borderRadius: 1,
                                      backgroundColor: 'transparent'
                                    }}
                                    onClick={(event) => { 
                                      event.stopPropagation();
                                      trackEvent('toggle_customizations', 'cart_interaction', item.name, undefined, {
                                        menu_item_id: item.menuItemId,
                                        action: isExpanded ? 'hide' : 'show'
                                      });
                                      toggleExpandItem(groupKey); 
                                    }}
                                  >
                                    <ExpandMoreIcon 
                                      fontSize="small" 
                                      sx={{ 
                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease',
                                        color: 'primary.main',
                                        mr: 0.5
                                      }} 
                                    />
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        color: 'primary.main', 
                                        fontWeight: 500,
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      {isExpanded ? 'Ocultar personalizaciones' : 'Ver personalizaciones'}
                                    </Typography>
                                  </Box>
                                )}

                                {/* Expanded customizations view */}
                                {isExpanded && item.selectedOptions && (
                                  <Box sx={{ mt: 1 }}>
                                    {Object.entries(item.selectedOptions).map(([groupName, choices]) => (
                                      choices.length > 0 && (
                                        <Box key={groupName} sx={{ mb: 0.5 }}>
                                          <Typography 
                                            variant="body2" 
                                            component="span" 
                                            sx={{ 
                                              color: 'text.primary', 
                                              fontWeight: 600, 
                                              fontSize: '0.8rem',
                                              mr: 0.5
                                            }}
                                          >
                                            {groupName}:
                                          </Typography>
                                          {choices.map((choice, index) => (
                                            <Typography 
                                              key={choice.name}
                                              variant="body2" 
                                              component="span" 
                                              sx={{ 
                                                color: 'text.secondary', 
                                                fontSize: '0.8rem',
                                                mr: index < choices.length - 1 ? 0.5 : 0
                                              }}
                                            >
                                              {choice.name}{(Number(choice.price) || 0) > 0 ? ` (+₡${(Number(choice.price) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) })` : ''}{index < choices.length - 1 ? ',' : ''}
                                            </Typography>
                                          ))}
                                        </Box>
                                      )
                                    ))}
                                  </Box>
                                )}

                                {item.notes && (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: 'text.secondary',
                                      fontSize: '0.8rem',
                                      mt: 0.5,
                                      fontStyle: 'italic'
                                    }}
                                  >
                                    Nota: {item.notes}
                                  </Typography>
                                )}
                              </Box>
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'row', 
                                  alignItems: 'center', 
                                  ml: 1, 
                                  minWidth: 0, 
                                  gap: 1, 
                                  position: 'relative', 
                                  width: 110, 
                                  justifyContent: 'center',
                                  '& *': { pointerEvents: 'auto' }
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {itemActionLoading[groupKey] ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 28 }}>
                                    <Loader size={20} />
                                  </Box>
                                ) : (
                                  <>
                                    <IconButton
                                      data-testid="decrease-qty-btn"
                                      size="small"
                                      onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                                      disabled={itemActionLoading[groupKey] || item.quantity <= 1 || !!(currentUser && item.user?.id !== currentUser.id)}
                                      sx={{ 
                                        p: 0, 
                                        m: 0, 
                                        width: 28, 
                                        height: 28, 
                                        borderRadius: '50%', 
                                        border: '1.5px solid #e0e0e0', 
                                        color: (currentUser && item.user?.id !== currentUser.id) ? 'rgba(0, 0, 0, 0.26)' : '#444', 
                                        background: '#fff', 
                                        transition: 'background 0.18s, border 0.18s', 
                                        '&:hover': { 
                                          background: (currentUser && item.user?.id !== currentUser.id) ? '#fff' : '#f3f5fa', 
                                          borderColor: (currentUser && item.user?.id !== currentUser.id) ? '#e0e0e0' : '#bfc8e6' 
                                        }, 
                                        '&:focus': { 
                                          background: (currentUser && item.user?.id !== currentUser.id) ? '#fff' : '#e6eaff', 
                                          borderColor: (currentUser && item.user?.id !== currentUser.id) ? '#e0e0e0' : '#4f8cff' 
                                        } 
                                      }}
                                    >
                                      <RemoveIcon fontSize="small" />
                                    </IconButton>
                                    <Typography 
                                      data-testid="item-quantity"
                                      sx={{ mx: 1, minWidth: 18, textAlign: 'center', fontWeight: 500, p: 0, m: 0 }}
                                    >
                                      {item.quantity}
                                    </Typography>
                                    <IconButton
                                      data-testid="increase-qty-btn"
                                      size="small"
                                      onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                                      disabled={itemActionLoading[groupKey] || !!(currentUser && item.user?.id !== currentUser.id)}
                                      sx={{ 
                                        p: 0, 
                                        m: 0, 
                                        width: 28, 
                                        height: 28, 
                                        borderRadius: '50%', 
                                        border: '1.5px solid #e0e0e0', 
                                        color: (currentUser && item.user?.id !== currentUser.id) ? 'rgba(0, 0, 0, 0.26)' : '#444', 
                                        background: '#fff', 
                                        transition: 'background 0.18s, border 0.18s', 
                                        '&:hover': { 
                                          background: (currentUser && item.user?.id !== currentUser.id) ? '#fff' : '#f3f5fa', 
                                          borderColor: (currentUser && item.user?.id !== currentUser.id) ? '#e0e0e0' : '#bfc8e6' 
                                        }, 
                                        '&:focus': { 
                                          background: (currentUser && item.user?.id !== currentUser.id) ? '#fff' : '#e6eaff', 
                                          borderColor: (currentUser && item.user?.id !== currentUser.id) ? '#e0e0e0' : '#4f8cff' 
                                        } 
                                      }}
                                    >
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                      <IconButton
                                      data-testid="delete-item-btn"
                                      size="small"
                                      onClick={() => {
                                        if (item.quantity > 1 && onRemoveGroup) {
                                          onRemoveGroup({
                                            menuItemId: item.menuItemId,
                                            notes: item.notes,
                                            selectedOptions: item.selectedOptions,
                                          });
                                        } else {
                                          onRemoveItem(item.id);
                                        }
                                      }}
                                      disabled={itemActionLoading[groupKey] || !!(currentUser && item.user?.id !== currentUser.id)}
                                      sx={{ 
                                        p: 0, 
                                        m: 0, 
                                        width: 28, 
                                        height: 28, 
                                        borderRadius: '50%', 
                                        border: '1.5px solid #e0e0e0', 
                                        color: (currentUser && item.user?.id !== currentUser.id) ? 'rgba(0, 0, 0, 0.26)' : '#d32f2f', 
                                        background: '#fff', 
                                        transition: 'background 0.18s, border 0.18s', 
                                        '&:hover': { 
                                          background: (currentUser && item.user?.id !== currentUser.id) ? '#fff' : '#fdecea', 
                                          borderColor: (currentUser && item.user?.id !== currentUser.id) ? '#e0e0e0' : '#f7c5c5' 
                                        }, 
                                        '&:focus': { 
                                          background: (currentUser && item.user?.id !== currentUser.id) ? '#fff' : '#fce4e4', 
                                          borderColor: (currentUser && item.user?.id !== currentUser.id) ? '#e0e0e0' : '#f8adad' 
                                        }, 
                                        ml: 1 
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </>
                                )}
                              </Box>
                            </ListItem>
                            <Divider sx={{ my: 1, mx: 1 }} />
                          </React.Fragment>
                        );
                      })}
                    </Collapse>
                </React.Fragment>
                );
              })
            )}
          </List>
        </Box>
        <Box sx={{ p: 3, pt: 2, boxShadow: '0 -2px 12px 0 rgba(60,72,100,0.08)', background: '#fff', pb: 'calc(16px + env(safe-area-inset-bottom))' }}>
          <Divider sx={{ mb: 2 }} />
          
          {/* Individual user totals */}
          <Box sx={{ position: 'relative' }}>
            <Box 
              sx={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: 1, 
                maxHeight: '60px', // 2 rows * ~30px per row
                overflowY: 'auto',
                mb: 2
              }}
            >
              {Object.entries(groupedCart).map(([userId, userItems]) => {
                const userObj = Object.values(userItems)[0]?.user;
                const userName = userObj?.name || 'Usuario Desconocido';
                const userTotal = Object.values(userItems).reduce(
                  (sum, item) => sum + (item.menuItem.price * item.quantity),
                  0
                );
                
                return (
                  <Box key={userId}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      Total de {userName}: ₡{userTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            
            {/* Floating scroll indicator */}
            {Object.keys(groupedCart).length > 4 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              >
                <KeyboardArrowDownIcon 
                  sx={{ 
                    color: 'white', 
                    fontSize: '16px',
                    animation: 'bounce 2s infinite',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }} 
                />
              </Box>
            )}
          </Box>
          
          {/* Table total */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2, pt: 1, borderTop: '1px solid #e0e0e0' }}>
            Total de la mesa: ₡{Number(total).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Typography>
          
          <Button
            variant="contained"
            fullWidth
            disabled={cart.length === 0 || Object.values(itemActionLoading).some(loading => loading)}
            onClick={onCheckout}
            sx={{ mt: 1 }}
          >
            Enviar a preparación
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CartDrawer; 